import ReactInputMask from 'react-input-mask';
import { InputStyle } from '../../../../components/InputStyle';
import { FormContainer } from './styles';
import { ButtonStyle } from '../../../../components/ButtonStyle';
import { useTitleAndUser } from '../../../../hooks/userTitle';
import { handleToast } from '../../../../components/Toast';
import axios from 'axios';
import { userRoutes } from '../../../../utils/RoutesApi';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { showZodErrorToast } from '../../../../utils/zodToast';
import { useEffect, useState } from 'react';
import {
  InfoValidationPassword,
  ValidationPassword,
} from '../../../pages/RegistrationStyles';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { Popover } from 'antd';

const validateCPF = (cpf: string) => {
  // Remover caracteres não numéricos
  cpf = cpf.replace(/[^\d]/g, '');

  // Checar se o CPF tem 11 dígitos
  if (cpf.length !== 11) return false;

  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpf)) return false;

  // Calcular os dígitos verificadores
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf[i]) * (10 - i);
  }

  let firstVerifier = (sum * 10) % 11;
  if (firstVerifier === 10 || firstVerifier === 11) {
    firstVerifier = 0;
  }

  if (firstVerifier !== parseInt(cpf[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf[i]) * (11 - i);
  }

  let secondVerifier = (sum * 10) % 11;
  if (secondVerifier === 10 || secondVerifier === 11) {
    secondVerifier = 0;
  }

  if (secondVerifier !== parseInt(cpf[10])) return false;

  return true;
};

const schema = z.object({
  cpf: z
    .string()
    .transform((val) => val.replace(/_/g, ''))
    .refine((val) => validateCPF(val), {
      message: 'CPF inválido',
    }),
  password: z.string(),
  passwordConfirm: z.string(),
});

interface IPropsFinishAccount {
  isB3Link?: boolean;
}

export function FinishAccountForm({ isB3Link = false }: IPropsFinishAccount) {
  // @ts-ignore
  const { user, setUser } = useTitleAndUser();
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
  });
  useEffect(() => {
    showZodErrorToast(errors);
  }, [errors]);
  async function submit(data: any) {
    const { cpf } = data;
    if (!cpf) {
      return handleToast(true, 'Informe seu cpf');
    }
    const { password } = data;
    if (!password) {
      return handleToast(true, 'Informe sua senha');
    }

    const { passwordConfirm } = data;
    if (!passwordConfirm) {
      return handleToast(true, 'Confirme sua senha');
    }

    if (password != passwordConfirm) {
      return handleToast(true, 'As senhas não coincidem');
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}${userRoutes.USER_FINISHED_REGISTRATION_HOTMART}/${user.id}/`,
        {
          cpf: cpf.toString(),
          password,
        }
      );
      setUser({
        ...user,
        cpf,
      });
    } catch (error) {
      const err: any = error;
      if (err.response.data.cpf) {
        return handleToast(true, err.response.data.cpf[0]);
      }
    } finally {
    }
  }
  const [viewPassword, setViewPassword] = useState(false);
  const [viewPasswordConfirm, setViewPasswordConfirm] = useState(false);

  const watchPasswords = watch('password');
  const watchPasswordConfirms = watch('passwordConfirm');

  const validateLettersInPassword = !!watchPasswords?.match(/[A-z]/);
  const validateNumberInPassword = !!watchPasswords?.match(/[0-9]/);
  const validateSpecialCharactersInPassword = !!watchPasswords?.match(
    /[!|@|#|$|%|^|&|*|(|)|-|_]/
  );
  const isValidPassword =
    watchPasswordConfirms &&
    watchPasswords &&
    watchPasswordConfirms === watchPasswords
      ? false
      : true;
  return (
    <FormContainer onSubmit={handleSubmit(submit)}>
      <p>
        {isB3Link
          ? 'Conecte sua conta à B3 inserindo o seu CPF para completar o cadastro.'
          : 'Para ter acesso a todas as funcionalidades do iBox⁶, por favor finalize seu cadastro.'}
      </p>
      <InputStyle label="CPF *" isError={errors.cpf ? true : false}>
        <ReactInputMask mask="999.999.999-99" {...register('cpf')} />
      </InputStyle>
      <Popover
        content={
          <ValidationPassword>
            <InfoValidationPassword error={watchPasswords?.length >= 8}>
              <span />
              Deve ter no mínimo 8 caracteres{' '}
            </InfoValidationPassword>

            <InfoValidationPassword error={validateNumberInPassword}>
              <span />
              Deve ter no mínimo 1 número
            </InfoValidationPassword>
            <InfoValidationPassword error={validateLettersInPassword}>
              <span />
              Deve ter no mínimo 1 letra
            </InfoValidationPassword>
            <InfoValidationPassword error={isValidPassword}>
              <span />
              Senha inválida
            </InfoValidationPassword>

            <InfoValidationPassword error={validateSpecialCharactersInPassword}>
              <span />
              Deve ter no mínimo 1 símbolo ex: @#$!{' '}
            </InfoValidationPassword>
          </ValidationPassword>
        }
        title="Crie uma senha segura"
        trigger="focus"
        zIndex={99999999}
      >
        <InputStyle
          label="Senha *"
          suffix={
            viewPassword ? (
              <Eye
                onClick={() => setViewPassword(!viewPassword)}
                size={20}
                style={{ cursor: 'pointer', color: '#6a717e' }}
              />
            ) : (
              <EyeSlash
                onClick={() => setViewPassword(!viewPassword)}
                size={20}
                style={{ cursor: 'pointer', color: '#6a717e' }}
              />
            )
          }
        >
          <input
            type={viewPassword ? 'text' : 'password'}
            {...register('password')}
            id="password"
            // placeholder="Crie uma senha"
          />
        </InputStyle>
      </Popover>

      <InputStyle
        label="Confirmar senha *"
        suffix={
          viewPasswordConfirm ? (
            <Eye
              onClick={() => setViewPasswordConfirm(!viewPasswordConfirm)}
              size={20}
              style={{ cursor: 'pointer', color: '#6a717e' }}
            />
          ) : (
            <EyeSlash
              onClick={() => setViewPasswordConfirm(!viewPasswordConfirm)}
              size={20}
              style={{ cursor: 'pointer', color: '#6a717e' }}
            />
          )
        }
      >
        <input
          type={viewPasswordConfirm ? 'text' : 'password'}
          {...register('passwordConfirm')}
          id="passwordConfirm"
          // placeholder="Confirme sua senha"
        />
      </InputStyle>

      <ButtonStyle type="submit">Cadastrar</ButtonStyle>
    </FormContainer>
  );
}
